import { Injectable } from '@angular/core';
import { Capacitor, registerPlugin } from '@capacitor/core';

export type HealthPlatformAvailabilityStatus =
  | 'available'
  | 'unsupported_os'
  | 'provider_update_required'
  | 'unavailable'
  | 'web';

export interface HealthPlatformAvailability {
  platform: 'healthConnect' | 'appleHealth' | 'web';
  available: boolean;
  capabilityEnabled: boolean;
  status: HealthPlatformAvailabilityStatus;
}

interface HealthIntegrationNativePlugin {
  getAvailability(): Promise<HealthPlatformAvailability>;
  requestAuthorization(options?: HealthAuthorizationOptions): Promise<HealthAuthorizationRequestResult>;
  getAuthorizationState(): Promise<AppleHealthAuthorizationState>;
  getDailyEnergySummary(options: { localDate: string }): Promise<DailyEnergySummaryResult>;
  upsertMeal(meal: HealthMealUpsert): Promise<HealthMealUpsertResult>;
  deleteMeal(options: HealthMealDelete): Promise<HealthMealUpsertResult>;
}

export interface HealthMealUpsert {
  stableIdentifier: string;
  version: number;
  name: string;
  startDate: string;
  energyKcal: number;
  proteinGrams?: number;
  carbohydrateGrams?: number;
  fatGrams?: number;
}

export interface HealthMealUpsertResult {
  status: 'saved' | 'failed';
  stableIdentifier: string;
  version: number;
}

export interface HealthMealDelete {
  stableIdentifier: string;
  version: number;
}

export interface HealthAuthorizationRequestResult {
  // HealthKit's read authorization remains intentionally opaque (`requested`).
  // Health Connect returns the scopes it actually granted (`granted`/`denied`).
  status: 'requested' | 'granted' | 'denied' | 'unavailable';
}
export interface HealthAuthorizationOptions {
  readActivity?: boolean;
  accessPastData?: boolean;
  shareMeals?: boolean;
}

export interface AppleHealthAuthorizationState {
  readAccess: 'requested_unknown' | 'unavailable';
  mealSharing: 'authorized' | 'denied' | 'not_requested' | 'partial' | 'unavailable';
}
export interface DailyEnergySummaryResult {
  status: 'data' | 'no_readable_data' | 'unavailable';
  localDate?: string;
  activeCalories?: number;
  totalCalories?: number;
  /** Provider aggregate versus the iOS active-plus-basal calculation. */
  totalCaloriesMethod?: 'provider_aggregate' | 'active_plus_basal';
  source?: string;
  observedAt?: string;
  observationStart?: string;
  observationEnd?: string;
  coverage?: 'current_day_partial' | 'complete';
}

const HealthIntegration = registerPlugin<HealthIntegrationNativePlugin>('HealthIntegration');

/**
 * A consent-free platform check. Capability gates remain false until physical
 * validation succeeds; consumers must not treat availability as authorization.
 */
@Injectable({ providedIn: 'root' })
export class HealthPlatformService {
  async getAvailability(): Promise<HealthPlatformAvailability> {
    if (Capacitor.getPlatform() === 'web') {
      return {
        platform: 'web',
        available: false,
        capabilityEnabled: false,
        status: 'web'
      };
    }

    return HealthIntegration.getAvailability();
  }

  /** Opens the native sheet only after a deliberate in-app tap. */
  async requestAuthorization(options?: HealthAuthorizationOptions): Promise<HealthAuthorizationRequestResult> {
    if (Capacitor.getPlatform() === 'web') return { status: 'unavailable' };
    return HealthIntegration.requestAuthorization(options);
  }

  /** Reads authorization metadata only; it never reads health samples. */
  async getAppleHealthAuthorizationState(): Promise<AppleHealthAuthorizationState | null> {
    if (Capacitor.getPlatform() === 'web') return null;
    return HealthIntegration.getAuthorizationState();
  }
  /** Reads the approved active/resting-energy aggregates for a selected day. */
  async getDailyEnergySummary(localDate: string): Promise<DailyEnergySummaryResult | null> {
    if (Capacitor.getPlatform() === 'web') return null;
    return HealthIntegration.getDailyEnergySummary({ localDate });
  }

  async upsertMeal(meal: HealthMealUpsert): Promise<HealthMealUpsertResult | null> {
    if (Capacitor.getPlatform() === 'web') return null;
    return HealthIntegration.upsertMeal(meal);
  }

  async deleteMeal(meal: HealthMealDelete): Promise<HealthMealUpsertResult | null> {
    if (Capacitor.getPlatform() === 'web') return null;
    return HealthIntegration.deleteMeal(meal);
  }
}
